// @steered AudibleSecurityContext 1.2 2026-09-21
import React from 'react';
import './ChatComponents.css';
import type { LiveCheckResult, SubmissionState, SubmissionStatus } from '../utils/submissionStatus';
import { PROPAGATION_GRACE_MS, isPastPropagationGrace } from '../utils/submissionStatus';

/**
 * The card that lives in the bottom slot of the chat from the moment a record
 * exists. It does NOT go away after submit: it turns into the status of the
 * change, because "did my change go out yet?" is a question users ask days
 * later, and a chat message has scrolled away by then.
 *
 *   ready      Submit button (+ whatever preview controls the parent passes in)
 *   in_review  Waiting on ORCA. Assumed state — there is no callback from the
 *              review/deploy process, so we say what happens next rather than
 *              pretend to track it.
 *   live       Verified: every record read back from the prod label matches.
 */

const STEPS = ['Created', 'Submitted', 'ORCA reviewing', 'Live'] as const;

const stepIndex = (state: SubmissionState): number => (state === 'ready' ? 1 : state === 'in_review' ? 2 : 3);

export interface SubmissionStatusCardProps {
    state: SubmissionState;
    status: SubmissionStatus;
    /** False once the server reports the session has no written records to check (pre-dates tracking). */
    canCheck: boolean;
    checking: boolean;
    lastCheck: LiveCheckResult | null;
    onCheckStatus: () => void;
    onSubmit: () => void;
    submitDisabled: boolean;
    submitLabel: string;
    /** Rendered inside the card in the `ready` state (preview surface selector, submit result). */
    children?: React.ReactNode;
    now?: number;
}

const formatWhen = (ms: number | null): string => {
    if (!ms) return '';
    const d = new Date(ms);
    return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
};

const STEP_STATE_TEXT = { done: 'completed', current: 'current step', todo: 'upcoming' } as const;

/**
 * Order-tracker style rail: a circle per step joined by connectors. Done steps are
 * filled green with a check, the current step is an amber ring that breathes slowly
 * (the only motion on the card), future steps are hollow. Rendered as a list so
 * screen readers get "step 3 of 4, current"; the visual check lives in the
 * aria-hidden node, so each label also carries visually-hidden state text.
 */
const ProgressRail = ({ current, done }: { current: number; done: boolean }) => (
    <ol className="g2s2-status-rail" aria-label="Submission progress">
        {STEPS.map((label, i) => {
            const cls = done || i < current ? 'done' : i === current ? 'current' : 'todo';
            return (
                <li key={label} className={`g2s2-status-step ${cls}`} aria-current={cls === 'current' ? 'step' : undefined}>
                    <span className="g2s2-status-node" aria-hidden="true">{cls === 'done' ? '✓' : ''}</span>
                    {i < STEPS.length - 1 && <span className={`g2s2-status-connector ${done || i < current ? 'done' : ''}`} aria-hidden="true" />}
                    <span className="g2s2-status-label">
                        {label}
                        <span className="g2s2-visually-hidden">, {STEP_STATE_TEXT[cls]}</span>
                    </span>
                </li>
            );
        })}
    </ol>
);

const SubmissionStatusCard = ({
    state, status, canCheck, checking, lastCheck, onCheckStatus, onSubmit, submitDisabled, submitLabel, children, now = Date.now(),
}: SubmissionStatusCardProps) => {
    const pastGrace = isPastPropagationGrace(status, now);
    const graceMinutes = Math.ceil(PROPAGATION_GRACE_MS / 60000);

    if (state === 'live') {
        return (
            <div className="g2s2-slack-submission-card g2s2-status-card live" data-testid="submission-status-card" data-state="live">
                <div className="g2s2-slack-submission-header">
                    <span className="g2s2-slack-icon">✅</span>
                    <span className="g2s2-slack-title">Your change is live</span>
                </div>
                <p className="g2s2-slack-description">
                    Every record from this session now reads back from production
                    {status.liveAt ? ` (confirmed ${formatWhen(status.liveAt)})` : ''}. Take one more look at the page to be sure it reads the way you expect.
                </p>
                <ProgressRail current={3} done />
            </div>
        );
    }

    if (state === 'in_review') {
        return (
            <div className="g2s2-slack-submission-card g2s2-status-card in-review" data-testid="submission-status-card" data-state="in_review">
                <div className="g2s2-slack-submission-header">
                    <span className="g2s2-slack-icon">⏳</span>
                    <span className="g2s2-slack-title">With the ORCA team for review</span>
                </div>
                <p className="g2s2-slack-description">
                    Submitted {formatWhen(status.submittedAt)}. You don't need to file anything — ORCA reviews your records and ships them in the
                    normal daily deployment (longer during Datapath restricted-day windows). Questions and updates land in the Slack thread your
                    submission created.
                </p>
                <ProgressRail current={2} done={false} />
                <div className="g2s2-status-check-row">
                    {!pastGrace ? (
                        <span className="g2s2-status-check-note" data-testid="status-grace-note">
                            Submitted just now — G2S2 changes take about {graceMinutes} minutes to appear even once deployed. Check back later.
                        </span>
                    ) : !canCheck ? (
                        <span className="g2s2-status-check-note" data-testid="status-nocheck-note">
                            This session predates automatic status checks — ask in the Slack thread, or verify on the page directly.
                        </span>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="g2s2-status-check-button"
                                onClick={onCheckStatus}
                                disabled={checking}
                                data-testid="check-status-button"
                            >
                                {checking ? 'Checking production…' : 'Check if it\'s live'}
                            </button>
                            <span className="g2s2-status-check-note" data-testid="status-check-result">
                                {lastCheck?.error
                                    ? `Couldn't read production (${lastCheck.error}). Try again in a bit.`
                                    : lastCheck
                                        ? `Not live yet — ${lastCheck.matched} of ${lastCheck.checked} records in production${status.lastCheckedAt ? ` · checked ${formatWhen(status.lastCheckedAt)}` : ''}`
                                        : 'We check production each time you open this session.'}
                            </span>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="g2s2-slack-submission-card g2s2-status-card ready" data-testid="submission-status-card" data-state="ready">
            <div className="g2s2-slack-submission-header">
                <span className="g2s2-slack-icon">📤</span>
                <span className="g2s2-slack-title">Ready to submit for review</span>
            </div>
            <p className="g2s2-slack-description">
                After checking your change in the preview, send it to the ORCA team. They review it and ship it in the next daily deployment — you
                don't need to file anything else. This card will track it from here.
            </p>
            <ProgressRail current={stepIndex('ready')} done={false} />
            {children}
            <button className="g2s2-slack-submit-button" onClick={onSubmit} disabled={submitDisabled} data-testid="submit-button">
                {submitLabel}
            </button>
        </div>
    );
};

export default SubmissionStatusCard;
